import React, { useState, useEffect, useRef } from "react";
import styles from "./budget-notes-modal.module.css";
import Input from "../../ui/input";
import { MyIcons } from "../../icons/my-icons";
import Button from "../../ui/button";
import Table from "../../ui/table";
import { getBudgetNotes, createBudgetNote, getBudgetNoteById, editBudgetNote, deleteBudgetNote, searchBudgetNotesByName } from "../../../services/apiService";
import { showErrorToast } from "../../../utils/notifications";

const BudgetNotesModal = ({ isOpen, onClose }) => {
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [budgetNotes, setBudgetNotes] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchBudgetNotes = async () => {
      try {
        const budgetNotesData = await getBudgetNotes();
        setBudgetNotes(budgetNotesData);
      } catch (error) {
        console.error("Error fetching budget notes:", error);
      }
    };

    if (isOpen) {
      fetchBudgetNotes();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddBudgetNote = async () => {
    try {
      // Validaciones
      if (!formData.name) {
        showErrorToast("El nombre es obligatorio");
        return;
      }

      const budgetNoteData = {
        nombre: formData.name,
        descripcion: formData.description
      };

      await createBudgetNote(budgetNoteData);
      
      // Refrescar la lista de notas de presupuesto
      const budgetNotesData = await getBudgetNotes();
      setBudgetNotes(budgetNotesData);
      
      // Limpiar el formulario
      setFormData({
        name: "",
        description: "",
      });
      
    } catch (error) {
      console.error("Error al crear nota de presupuesto:", error);
    }
  };

  const handleEditBudgetNote = async () => {
    try {
      // Validaciones
      if (!formData.name) {
        showErrorToast("El nombre es obligatorio");
        return;
      }

      const budgetNoteData = {
        nombre: formData.name,
        descripcion: formData.description
      };

      await editBudgetNote(editingNoteId, budgetNoteData);

      // Refrescar la lista de notas de presupuesto
      const budgetNotesData = await getBudgetNotes();
      setBudgetNotes(budgetNotesData);

      // Limpiar el formulario y salir del modo edición
      setFormData({
        name: "",
        description: "",
      });
      setIsEditMode(false);
      setEditingNoteId(null);
    } catch (error) {
      console.error("Error al editar nota de presupuesto:", error);
    }
  };

  const handleNoteRowClick = async (note) => {
    try {
      const noteDetails = await getBudgetNoteById(note.id);

      // Llenar el formulario con los datos de la nota
      setFormData({
        name: noteDetails.nombre || "",
        description: noteDetails.descripcion || "",
      });

      setIsEditMode(true);
      setEditingNoteId(note.id);
      
      // Hacer scroll al inicio del modal y focus en el primer input
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.scrollTop = 0;
        }
        if (firstInputRef.current) {
          firstInputRef.current.focus();
        }
      }, 100);
    } catch (error) {
      console.error("Error al cargar los detalles de la nota:", error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    console.log("Eliminar nota con ID:", noteId);
    // Aquí se implementará la eliminación cuando esté lista en el backend
  };

  const handleCancelEdit = () => {
    setFormData({
      name: "",
      description: "",
    });
    setIsEditMode(false);
    setEditingNoteId(null);
  };

  const handleSearchBudgetNotes = async (value) => {
    setSearchTerm(value);
    
    if (!value || value.trim() === "") {
      // Si no hay término de búsqueda, cargar todas las notas de presupuesto
      try {
        const budgetNotesData = await getBudgetNotes();
        setBudgetNotes(budgetNotesData);
      } catch (error) {
        console.error("Error fetching budget notes:", error);
      }
      return;
    }

    // Buscar notas de presupuesto por nombre
    try {
      const searchResults = await searchBudgetNotesByName(value);
      setBudgetNotes(searchResults);
    } catch (error) {
      console.error("Error searching budget notes:", error);
    }
  };

  const headers = [
    { label: "Nombre", key: "nombre" },
    { label: "Descripción", key: "descripcion" },
  ];

  // Transformar los datos para formatear la descripción
  const budgetNotesForTable = budgetNotes.map(note => ({
    id: note.id,
    nombre: note.nombre,
    descripcion: (
      <div className={styles.descriptionCell} title={note.descripcion || ""}>
        {note.descripcion || ""}
      </div>
    )
  }));

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer} ref={modalRef}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Notas de presupuesto</h3>
          <button className={styles.closeButton} onClick={onClose}>
            <MyIcons
              name="close"
              width={24}
              height={24}
              color="black"
            />
          </button>
        </div>

        <div className={styles.formSection}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "16px",
              width: "100%",
            }}
          >
            <Input
              ref={firstInputRef}
              label="Nombre"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              style={{ flex: 1, maxWidth: "320px" }}
            />
          </div>
          
          <span className={styles.textareaLabel}>Descripción</span>
          <textarea
            name="description"
            placeholder="Descripción de la nota"
            value={formData.description}
            onChange={handleInputChange}
            className={styles.descriptionTextarea}
            style={{
              width: "100%",
              height: "200px",
              paddingTop: "12px",
              paddingBottom: "12px",
              paddingLeft: "12px",
              paddingRight: "12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontFamily: "var(--font-roboto)",
              fontSize: "14px",
              color: "black",
              resize: "none",
              outline: "none",
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              overflow: "auto",
            }}
            spellCheck="true"
          />
        </div>

        <div style={{ marginBottom: "16px", display: "flex", gap: "16px" }}>
          {isEditMode ? (
            <>
              <Button type="button" variant="primary" onClick={handleEditBudgetNote}>
                EDITAR NOTA
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancelEdit}>
                CANCELAR
              </Button>
            </>
          ) : (
            <Button type="button" variant="primary" onClick={handleAddBudgetNote}>
              AÑADIR NOTA
            </Button>
          )}
        </div>

        <div className={styles.tableSection}>
          <Input
            label="Buscar nota de presupuesto"
            placeholder="Buscar por nombre..."
            value={searchTerm}
            onChange={(e) => handleSearchBudgetNotes(e.target.value)}
            style={{ marginBottom: "16px" }}
          />
          <Table 
            headers={headers} 
            data={budgetNotesForTable}
            onPress={handleNoteRowClick}
          />
        </div>
      </div>
    </div>
  );
};

export default BudgetNotesModal;
